import { ArrowLeft, LogOut, Pencil } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ASSET_CONDITION_LABELS,
  CLEARANCE_STATUS_LABELS,
  canRecordExit,
  EMPLOYEE_STATUS_LABELS,
  formatPlainDate,
  ROLE_LABELS,
  tidyText,
} from '@cfl/assets-shared';

import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/cargofl-ui';
import { FullPageSpinner } from '@/components/shell/full-page-spinner';
import { PageContainer } from '@/components/shell/page-container';
import { clearanceVariant, conditionVariant, employeeStatusVariant, PILL_CLASS } from '@/lib/badges';
import { useAssets } from '@/lib/assets-api';
import { useEmployee } from '@/lib/employees-api';
import { useEmployeeAssignments } from '@/lib/operations-api';
import { EmployeeFormDialog } from '@/routes/employee-form';
import { RecordExitDialog } from '@/routes/exits';
import { ReturnDialog, type ReturnTarget } from '@/routes/return';
import { useCan } from '@/lib/session';

export function EmployeeDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { data: employee, isPending } = useEmployee(employeeId ?? null);
  const { data: assignments } = useEmployeeAssignments(employeeId ?? null);
  const canManage = useCan('manageEmployees');
  const canExit = useCan('runExitClearance');
  const canTakeBack = useCan('assignAssets');
  const [editing, setEditing] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);
  const [returning, setReturning] = React.useState<ReturnTarget | null>(null);

  /**
   * The same rows again, as the register sees them.
   *
   * The return form needs `categoryId` and `ownership` to decide whether to ask for the
   * device login, and the assignment payload carries neither. An AssetSummary IS a
   * ReturnTarget, so the register's own filtered query supplies it without widening an
   * API that four other screens read.
   */
  const { data: heldNow } = useAssets(
    { holderId: employeeId ?? '', pageSize: 100 },
    { enabled: Boolean(employeeId) && canTakeBack },
  );

  if (isPending) return <FullPageSpinner />;
  if (!employee) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-12 text-center sm:py-12">
            <p className="text-sm text-muted-foreground">That employee no longer exists.</p>
            <Button asChild variant="secondary" size="sm" className="mt-4">
              <Link to="/employees">Back to the directory</Link>
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const open = assignments?.filter((a) => a.isOpen) ?? [];
  const past = assignments?.filter((a) => !a.isOpen) ?? [];

  return (
    <PageContainer>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1 h-7 text-muted-foreground">
          <Link to="/employees">
            <ArrowLeft className="mr-1 size-3.5" /> Directory
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-bold tracking-[-0.02em]">{employee.name}</h2>
          <Badge variant={employeeStatusVariant(employee.status)} className={PILL_CLASS}>
            {EMPLOYEE_STATUS_LABELS[employee.status]}
          </Badge>
        </div>
          <p className="at-num text-sm text-muted-foreground">
            {employee.employeeCode}
            {employee.department ? ` · ${employee.department}` : ''}
            {employee.designation ? ` · ${employee.designation}` : ''}
            {` · ${ROLE_LABELS[employee.role]}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canManage && (
            <Button hint={"Change this employee's details"} size="sm" variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 size-4" /> Edit
            </Button>
          )}
          {/* This screen already lists everything they hold, which is the whole
              conversation an exit is about - and it offered no way to record one.
              Hidden once they have left: the exit is the thing already done. */}
          {canExit && canRecordExit(employee.status) && (
            <Button hint={"Record that this person is leaving, with what they must hand back"} size="sm" variant="secondary" onClick={() => setExiting(true)}>
              <LogOut className="mr-1.5 size-4" /> Record exit
            </Button>
          )}
        </div>
      </div>

      <RecordExitDialog
        employee={employee}
        open={exiting}
        onOpenChange={setExiting}
      />

      {employee.notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="whitespace-pre-wrap text-sm">{tidyText(employee.notes)}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            Currently holding {open.length > 0 ? `(${open.length})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {open.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing is issued to this person.
            </p>
          ) : (
            <ul className="divide-y">
              {open.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <Link to={`/assets/${a.asset.id}`} className="min-w-0 flex-1 hover:underline">
                    <div className="text-sm font-medium">{a.asset.name}</div>
                    <div className="at-num text-xs text-muted-foreground">
                      {a.asset.code}
                      {a.asset.serial ? ` · ${a.asset.serial}` : ''}
                      {` · since ${formatPlainDate(a.assignedAt)}`}
                    </div>
                  </Link>
                  <Badge variant={conditionVariant(a.condition)} className={PILL_CLASS}>
                    {ASSET_CONDITION_LABELS[a.condition]}
                  </Badge>
                  {a.acknowledgedAt === null && (
                    <Badge variant="soft-warning" className={PILL_CLASS}>
                      not confirmed
                    </Badge>
                  )}
                  {/* The step this screen was missing.
                      Exit & clearance says "recover these, then update the clearance"
                      and links straight here - and then there was nothing to press, so
                      a recovered laptop got written into the clearance comments instead
                      of being returned. The register could always do it; the person
                      doing the exit was just never on that screen. */}
                  {canTakeBack &&
                    (() => {
                      const target = heldNow?.assets.find((row) => row.id === a.asset.id);
                      return target ? (
                        <Button hint={"Record that this asset came back"}
                          variant="secondary"
                          size="sm"
                          onClick={() => setReturning(target)}
                        >
                          Return
                        </Button>
                      ) : null;
                    })()}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {past.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Previously held</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y">
              {past.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <Link to={`/assets/${a.asset.id}`} className="min-w-0 flex-1 hover:underline">
                    <div className="text-sm">{a.asset.name}</div>
                    <div className="at-num text-xs text-muted-foreground">
                      {a.asset.code} · {formatPlainDate(a.assignedAt)} →{' '}
                      {a.returnedAt ? formatPlainDate(a.returnedAt) : '—'}
                    </div>
                  </Link>
                  {a.returnCondition && (
                    <Badge variant={conditionVariant(a.returnCondition)} className={PILL_CLASS}>
                      {ASSET_CONDITION_LABELS[a.returnCondition]}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {employee.exits.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Exit &amp; clearance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {employee.exits.map((exit) => (
              <div key={exit.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="at-num text-sm font-medium">
                    {formatPlainDate(exit.exitDate)}
                  </span>
                  <Badge variant={clearanceVariant(exit.clearanceStatus)} className={PILL_CLASS}>
                    {CLEARANCE_STATUS_LABELS[exit.clearanceStatus]}
                  </Badge>
                  {exit.outstandingAtClearance > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {exit.outstandingAtClearance} outstanding at the time
                    </span>
                  )}
                </div>
                {exit.reason && <p className="mt-1 text-xs text-muted-foreground">{exit.reason}</p>}
                {exit.comments && (
                  <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed">
                    {tidyText(exit.comments)}
                  </p>
                )}
                {exit.recordedByName && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Recorded by {exit.recordedByName}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <EmployeeFormDialog open={editing} onOpenChange={setEditing} employee={employee} />
      {returning && (
        <ReturnDialog asset={returning} onOpenChange={(open) => !open && setReturning(null)} />
      )}
    </PageContainer>
  );
}
